import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbSize, AbSizeContext } from "abreact/misc/AbSize";
import * as AbHooks from "abreact/utils/AbHooks";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import * as React from "react";
import { AbToolbarContext } from "abreact/layout/toolbar/AbToolbar";

/**
 * The properties for an AbTextInput.
 */

/**
 * The **AbTextInput** component allows the user to enter a single line of text.
 */
export const AbTextInput = props => {
  const uniqueId = AbHooks.useUniqueId("ab-text-input");
  const {
    name,
    value,
    tooltip,
    placeholder,
    readOnly
  } = props;
  const contextSize = React.useContext(AbSizeContext);
  const inputProps = ReactUtils.inputProps("ab-text-input", props, ["abui", "ab-single-line", props.size ?? contextSize ?? AbSize.MEDIUM]);
  const {
    id,
    className,
    style,
    disabled,
    tabIndex,
    autoFocus
  } = inputProps;
  const inputRef = AbHooks.useInputRef(props);
  const inToolbar = React.useContext(AbToolbarContext);

  // Note: there is a Chrome bug where disabled React controls do not fire mouse leave events.
  // To work around this, we don't disable inputs with tooltips explicitly, but instead remove the
  // focus and onClick handling, and visually display the input as disabled.
  //
  // One downside to this approach is that for screen readers and other accessibility tools,
  // the input does not appear to be disabled. This may be something we have to address
  // in the future.
  //
  // See: https://jira.abinitio.com/browse/CAT-1270 and https://github.com/facebook/react/issues/4251
  const useFakeDisabling = disabled && tooltip;
  const onChange = event => {
    props.onChange(event.target.value);
  };
  const onKeyDown = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ENTER, props.onEnter, true], [KeyValue.ESCAPE, props.onEscape, true], [[KeyValue.ARROW_LEFT, KeyValue.HOME], e => {
    if (inToolbar && e.currentTarget.selectionStart !== 0) {
      e.stopPropagation(); // Let focus escape when in toolbar
    }
  }, true], [[KeyValue.ARROW_RIGHT, KeyValue.END], e => {
    if (inToolbar && e.currentTarget.selectionStart !== value.length) {
      e.stopPropagation(); // Let focus escape when in toolbar
    }
  }, true]]);
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: tooltip
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: id ?? uniqueId,
    className: className,
    style: style,
    "data-test-id": props.dataTestId,
    "aria-disabled": disabled,
    name: name,
    value: value,
    maxLength: props.maxLength,
    placeholder: placeholder,
    disabled: useFakeDisabling ? false : disabled,
    readOnly: readOnly,
    tabIndex: useFakeDisabling ? -1 : tabIndex,
    onChange: disabled ? undefined : onChange,
    autoFocus: autoFocus,
    onBlur: disabled ? undefined : props.onBlur,
    onFocus: disabled ? undefined : props.onFocus,
    ref: inputRef,
    onDragOver: props.onDragOver
  }, props.__htmlProps, {
    type: props.__htmlProps?.type ?? "text",
    onKeyDown: disabled ? undefined : e => {
      props.__htmlProps?.onKeyDown?.(e);
      onKeyDown(e);
    }
  })));
};
//# sourceMappingURL=AbTextInput.js.map
